import { supabase } from './supabase';

/**
 * Cendien Training Academy — data layer.
 *
 * A course is a container of ordered modules, each holding ordered lessons.
 * Access is enforced by Supabase row-level security: anonymous visitors can
 * only read courses that are `published` + `public`; admins (and, in a later
 * phase, code-unlocked learners) can read the rest. This module only maps rows
 * and shapes queries — it never bypasses RLS.
 */

export type AcademyVisibility = 'public' | 'gated';
export type AcademyStatus = 'draft' | 'published';

export const ACADEMY_LEVELS = ['Fundamentals', 'Intermediate', 'Advanced'] as const;
export const ACADEMY_VISIBILITIES: AcademyVisibility[] = ['public', 'gated'];
export const ACADEMY_STATUSES: AcademyStatus[] = ['draft', 'published'];
export const ACADEMY_VIDEO_PROVIDERS = ['none', 'stream', 'mp4', 'youtube'] as const;
export const ACADEMY_AVAILABILITY = ['available', 'in-development', 'planned'] as const;
export const ACADEMY_CONTENT_TYPES = ['course', 'clip', 'tutorial', 'webinar', 'assessment'] as const;
export const ACADEMY_DELIVERY_FORMATS = ['video', 'instructor-led', 'blended', 'self-paced'] as const;
export const ACADEMY_LEARNER_ROLES = ['Business user', 'Consultant', 'Administrator', 'Developer', 'Support team', 'Project manager', 'Client organization'] as const;
export const ACADEMY_LANGUAGES = ['en'] as const;
/** Accent palette for platform tiles (matches the public accent map — no blue/purple). */
export const ACADEMY_PLATFORM_ACCENTS = ['teal', 'orange', 'amber', 'emerald', 'rose', 'slate'] as const;
/** A curated set of Remix icons an admin can pick for a platform tile. */
export const ACADEMY_PLATFORM_ICONS = [
  'ri-database-2-line',
  'ri-cloud-line',
  'ri-shield-keyhole-line',
  'ri-cpu-line',
  'ri-layout-grid-line',
  'ri-bar-chart-2-line',
  'ri-flow-chart',
  'ri-group-line',
  'ri-briefcase-line',
  'ri-server-line',
  'ri-code-s-slash-line',
  'ri-store-2-line',
  'ri-building-2-line',
  'ri-graduation-cap-line',
  'ri-line-chart-line',
  'ri-settings-3-line',
] as const;

export interface AcademyCourse {
  id: string;
  slug: string;
  title: string;
  summary: string;
  description: string;
  topic: string;
  level: string;
  visibility: AcademyVisibility;
  status: AcademyStatus;
  coverImage: string;
  seoTitle: string;
  seoDescription: string;
  position: number;
  estimatedMinutes: number;
  // ── Extensible dimension attributes (managed in the admin) ──
  product: string;
  learnerRole: string;
  industry: string;
  deliveryFormat: string;
  contentType: string;
  language: string;
  versionApplicability: string;
  availability: string;
  /** Platform slugs this course is linked to (many-to-many, supports cross-listing). */
  platformSlugs: string[];
  /** Topic slugs this course is linked to. */
  topicSlugs: string[];
  /**
   * Lesson count resolved for list views. The public catalog counts published
   * lessons only; the admin list counts every lesson (drafts included) so a
   * course never looks empty while it is being built.
   */
  lessonCount?: number;
}

export interface AcademyLesson {
  id: string;
  courseId: string;
  moduleId: string | null;
  slug: string;
  title: string;
  summary: string;
  content: string;
  coverImage: string;
  videoProvider: string;
  videoId: string;
  durationSeconds: number;
  position: number;
  status: AcademyStatus;
}

export interface AcademyModule {
  id: string;
  courseId: string;
  title: string;
  summary: string;
  position: number;
  lessons: AcademyLesson[];
}

export interface AcademyCourseDetail extends AcademyCourse {
  modules: AcademyModule[];
  /** Flat, ordered list of every lesson in the course. */
  lessons: AcademyLesson[];
}

export interface AcademyPlatform {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  summary: string;
  description: string;
  icon: string;
  accent: string;
  /** Optional brand logo (transparent PNG/SVG) shown on the tile + page hero. */
  logoUrl: string;
  /** Optional wide header illustration shown behind the platform page hero. */
  headerImage: string;
  position: number;
  status: AcademyStatus;
  /** Number of published courses linked to this platform (list views). */
  courseCount?: number;
}

export interface AcademyTopic {
  id: string;
  slug: string;
  name: string;
  /** Short one-line description shown in the admin hover cards. */
  summary: string;
  position: number;
}

type Row = Record<string, unknown>;

const str = (v: unknown, fallback = ''): string => (v == null ? fallback : String(v));
const num = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// ── Row mappers ─────────────────────────────────────────────────────────────

export function mapCourse(raw: Row): AcademyCourse {
  return {
    id: str(raw.id),
    slug: str(raw.slug),
    title: str(raw.title),
    summary: str(raw.summary),
    description: str(raw.description),
    topic: str(raw.topic),
    level: str(raw.level, 'Fundamentals'),
    visibility: raw.visibility === 'public' ? 'public' : 'gated',
    status: raw.status === 'published' ? 'published' : 'draft',
    coverImage: str(raw.cover_image),
    seoTitle: str(raw.seo_title),
    seoDescription: str(raw.seo_description),
    position: num(raw.position),
    estimatedMinutes: num(raw.estimated_minutes),
    product: str(raw.product),
    learnerRole: str(raw.learner_role),
    industry: str(raw.industry),
    deliveryFormat: str(raw.delivery_format),
    contentType: str(raw.content_type),
    language: str(raw.language, 'en'),
    versionApplicability: str(raw.version_applicability),
    availability: str(raw.availability, 'available'),
    platformSlugs: [],
    topicSlugs: [],
  };
}

export function mapLesson(raw: Row): AcademyLesson {
  return {
    id: str(raw.id),
    courseId: str(raw.course_id),
    moduleId: raw.module_id ? str(raw.module_id) : null,
    slug: str(raw.slug),
    title: str(raw.title),
    summary: str(raw.summary),
    content: str(raw.content),
    coverImage: str(raw.cover_image),
    videoProvider: str(raw.video_provider, 'none'),
    videoId: str(raw.video_id),
    durationSeconds: num(raw.duration_seconds),
    position: num(raw.position),
    status: raw.status === 'published' ? 'published' : 'draft',
  };
}

export function mapModule(raw: Row, lessons: AcademyLesson[]): AcademyModule {
  const id = str(raw.id);
  return {
    id,
    courseId: str(raw.course_id),
    title: str(raw.title),
    summary: str(raw.summary),
    position: num(raw.position),
    lessons: lessons
      .filter((l) => l.moduleId === id)
      .sort((a, b) => a.position - b.position),
  };
}

export function mapPlatform(raw: Row): AcademyPlatform {
  return {
    id: str(raw.id),
    slug: str(raw.slug),
    name: str(raw.name),
    vendor: str(raw.vendor),
    summary: str(raw.summary),
    description: str(raw.description),
    icon: str(raw.icon, 'ri-book-open-line'),
    accent: str(raw.accent, 'teal'),
    logoUrl: str(raw.logo_url),
    headerImage: str(raw.header_image),
    position: num(raw.position),
    status: raw.status === 'published' ? 'published' : 'draft',
  };
}

export function mapTopic(raw: Row): AcademyTopic {
  return {
    id: str(raw.id),
    slug: str(raw.slug),
    name: str(raw.name),
    summary: str(raw.summary),
    position: num(raw.position),
  };
}

/** Attaches platform + topic slugs to a set of courses (tolerant of failure). */
async function attachTaxonomy(courses: AcademyCourse[]): Promise<void> {
  if (!courses.length) return;
  try {
    const [platLinks, platRows, topicLinks, topicRows] = await Promise.all([
      supabase.from('academy_course_platforms').select('course_id, platform_id, position'),
      supabase.from('academy_platforms').select('id, slug'),
      supabase.from('academy_course_topics').select('course_id, topic_id'),
      supabase.from('academy_topics').select('id, slug'),
    ]);
    const platSlug = new Map<string, string>();
    (platRows.data || []).forEach((r) => platSlug.set(str((r as Row).id), str((r as Row).slug)));
    const topicSlug = new Map<string, string>();
    (topicRows.data || []).forEach((r) => topicSlug.set(str((r as Row).id), str((r as Row).slug)));

    const byCourseP = new Map<string, { slug: string; position: number }[]>();
    (platLinks.data || []).forEach((r) => {
      const row = r as Row;
      const slug = platSlug.get(str(row.platform_id));
      if (!slug) return;
      const cid = str(row.course_id);
      const list = byCourseP.get(cid) || [];
      list.push({ slug, position: num(row.position) });
      byCourseP.set(cid, list);
    });
    const byCourseT = new Map<string, string[]>();
    (topicLinks.data || []).forEach((r) => {
      const row = r as Row;
      const slug = topicSlug.get(str(row.topic_id));
      if (!slug) return;
      const cid = str(row.course_id);
      const list = byCourseT.get(cid) || [];
      list.push(slug);
      byCourseT.set(cid, list);
    });
    courses.forEach((c) => {
      const p = byCourseP.get(c.id);
      c.platformSlugs = p ? p.sort((a, b) => a.position - b.position).map((x) => x.slug) : [];
      c.topicSlugs = byCourseT.get(c.id) || [];
    });
  } catch {
    courses.forEach((c) => { c.platformSlugs = c.platformSlugs || []; c.topicSlugs = c.topicSlugs || []; });
  }
}

// ── Queries ─────────────────────────────────────────────────────────────────

/**
 * Attaches a lesson count to each course. When `publishedOnly` is set only
 * published lessons are counted (public catalog); otherwise every lesson is
 * counted (admin list). Failures leave the count at 0 so the column still
 * renders instead of vanishing.
 */
async function attachLessonCounts(
  courses: AcademyCourse[],
  opts: { publishedOnly?: boolean } = {},
): Promise<void> {
  if (!courses.length) return;
  try {
    let query = supabase.from('academy_lessons').select('course_id');
    if (opts.publishedOnly) query = query.eq('status', 'published');
    const { data, error } = await query;
    if (error) throw error;
    const counts = new Map<string, number>();
    (data || []).forEach((l) => {
      const id = str((l as Row).course_id);
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    courses.forEach((c) => { c.lessonCount = counts.get(c.id) || 0; });
  } catch {
    courses.forEach((c) => { if (c.lessonCount === undefined) c.lessonCount = 0; });
  }
}

/** Published courses visible to the current visitor (RLS applies). */
export async function fetchPublishedCourses(): Promise<AcademyCourse[]> {
  const { data, error } = await supabase
    .from('academy_courses')
    .select('*')
    .eq('status', 'published')
    .order('position', { ascending: true })
    .order('title', { ascending: true });
  if (error) throw error;
  const courses = (data || []).map((r) => mapCourse(r as Row));
  await attachLessonCounts(courses, { publishedOnly: true });
  await attachTaxonomy(courses);
  return courses;
}

/** Loads the modules + lessons for a course and assembles the full detail. */
async function hydrateCourse(course: AcademyCourse): Promise<AcademyCourseDetail> {
  const [{ data: moduleRows }, { data: lessonRows }] = await Promise.all([
    supabase.from('academy_modules').select('*').eq('course_id', course.id).order('position', { ascending: true }),
    supabase.from('academy_lessons').select('*').eq('course_id', course.id).order('position', { ascending: true }),
  ]);

  const rawLessons = (lessonRows || []).map((r) => mapLesson(r as Row));
  const modules = (moduleRows || [])
    .map((r) => mapModule(r as Row, rawLessons))
    .sort((a, b) => a.position - b.position);
  const orphan = rawLessons.filter((l) => !l.moduleId).sort((a, b) => a.position - b.position);
  if (orphan.length) {
    modules.push({ id: 'ungrouped', courseId: course.id, title: 'Lessons', summary: '', position: 9999, lessons: orphan });
  }
  // The flat list must follow module order first, then lesson position within
  // each module. Lesson `position` is only unique *within* a module, so sorting
  // it globally interleaves modules and makes prev/next jump across chapters.
  const lessons = modules.flatMap((m) => m.lessons);
  return { ...course, lessonCount: lessons.length, modules, lessons };
}

/** A single course with its modules and lessons, or null when not visible. */
export async function fetchCourseDetail(slug: string): Promise<AcademyCourseDetail | null> {
  const { data: courseRow, error } = await supabase
    .from('academy_courses')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  if (!courseRow) return null;
  return hydrateCourse(mapCourse(courseRow as Row));
}

/** A single course (by id) with modules + lessons — admin editor. */
export async function fetchCourseById(id: string): Promise<AcademyCourseDetail | null> {
  const { data: courseRow, error } = await supabase
    .from('academy_courses')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!courseRow) return null;
  return hydrateCourse(mapCourse(courseRow as Row));
}

/** Every course regardless of status/visibility (admin list). */
export async function fetchAllCourses(): Promise<AcademyCourse[]> {
  const { data, error } = await supabase
    .from('academy_courses')
    .select('*')
    .order('position', { ascending: true })
    .order('title', { ascending: true });
  if (error) throw error;
  const courses = (data || []).map((r) => mapCourse(r as Row));
  await Promise.all([attachTaxonomy(courses), attachLessonCounts(courses)]);
  return courses;
}

/** Published platforms (RLS-scoped) with published-course counts attached. */
export async function fetchAcademyPlatforms(): Promise<AcademyPlatform[]> {
  const { data, error } = await supabase
    .from('academy_platforms')
    .select('*')
    .eq('status', 'published')
    .order('position', { ascending: true });
  if (error) throw error;
  const platforms = (data || []).map((r) => mapPlatform(r as Row));
  const courses = await fetchPublishedCourses();
  const counts = new Map<string, number>();
  courses.forEach((c) => c.platformSlugs.forEach((s) => counts.set(s, (counts.get(s) || 0) + 1)));
  platforms.forEach((p) => { p.courseCount = counts.get(p.slug) || 0; });
  return platforms;
}

/** A single platform by slug, plus the published courses linked to it. */
export async function fetchAcademyPlatform(
  slug: string,
): Promise<{ platform: AcademyPlatform; courses: AcademyCourse[] } | null> {
  const { data, error } = await supabase
    .from('academy_platforms')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const platform = mapPlatform(data as Row);
  const courses = (await fetchPublishedCourses()).filter((c) => c.platformSlugs.includes(slug));
  platform.courseCount = courses.length;
  return { platform, courses };
}

// ── Platform admin + course linking ─────────────────────────────────────────

/**
 * Admin listing of every platform regardless of status, ordered for display.
 * The public catalog only returns published rows; the admin needs the drafts too.
 */
export async function fetchAllPlatforms(): Promise<AcademyPlatform[]> {
  const { data, error } = await supabase
    .from('academy_platforms')
    .select('*')
    .order('position', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => mapPlatform(r as Row));
}

export interface PlatformCourseCounts {
  /** Courses linked to the platform whose status is published. */
  published: number;
  /** Every course linked to the platform, drafts included. */
  total: number;
}

/**
 * Course-link counts for every platform, keyed by platform id — used by the
 * admin Platforms manager so an editor can see WHY a tile shows a given count
 * (e.g. all links point at unpublished drafts).
 */
export async function fetchPlatformCourseCounts(): Promise<Map<string, PlatformCourseCounts>> {
  const [linksRes, coursesRes] = await Promise.all([
    supabase.from('academy_course_platforms').select('platform_id, course_id'),
    supabase.from('academy_courses').select('id, status'),
  ]);
  const statusById = new Map<string, string>();
  (coursesRes.data || []).forEach((r) => statusById.set(str((r as Row).id), str((r as Row).status)));
  const map = new Map<string, PlatformCourseCounts>();
  (linksRes.data || []).forEach((r) => {
    const row = r as Row;
    const pid = str(row.platform_id);
    const entry = map.get(pid) || { published: 0, total: 0 };
    entry.total += 1;
    if (statusById.get(str(row.course_id)) === 'published') entry.published += 1;
    map.set(pid, entry);
  });
  return map;
}

/** The platform ids a course is currently linked to (ordered). */
export async function fetchCoursePlatformIds(courseId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('academy_course_platforms')
    .select('platform_id, position')
    .eq('course_id', courseId)
    .order('position', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => str((r as Row).platform_id));
}

/** Replaces a course's platform links with exactly the given ordered platform ids. */
export async function setCoursePlatforms(courseId: string, platformIds: string[]): Promise<void> {
  const { error: delErr } = await supabase
    .from('academy_course_platforms')
    .delete()
    .eq('course_id', courseId);
  if (delErr) throw delErr;
  if (!platformIds.length) return;
  const rows = platformIds.map((platformId, index) => ({
    course_id: courseId,
    platform_id: platformId,
    position: index,
  }));
  const { error } = await supabase.from('academy_course_platforms').insert(rows);
  if (error) throw error;
}

export interface PlatformInput {
  slug: string;
  name: string;
  vendor: string;
  summary: string;
  description: string;
  icon: string;
  accent: string;
  logoUrl: string;
  headerImage: string;
  position: number;
  status: AcademyStatus;
}

function platformRow(input: PlatformInput): Row {
  return {
    slug: input.slug,
    name: input.name,
    vendor: input.vendor,
    summary: input.summary,
    description: input.description,
    icon: input.icon,
    accent: input.accent,
    logo_url: input.logoUrl,
    header_image: input.headerImage,
    position: input.position,
    status: input.status,
    updated_at: new Date().toISOString(),
  };
}

export async function insertPlatform(input: PlatformInput): Promise<AcademyPlatform> {
  const { data, error } = await supabase.from('academy_platforms').insert(platformRow(input)).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Platform was not created');
  return mapPlatform(data as Row);
}

export async function updatePlatform(id: string, input: PlatformInput): Promise<AcademyPlatform> {
  const { data, error } = await supabase
    .from('academy_platforms')
    .update(platformRow(input))
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Platform was not updated');
  return mapPlatform(data as Row);
}

/** Deletes a platform. Its course links cascade away with it. */
export async function deletePlatform(id: string): Promise<void> {
  const { error } = await supabase.from('academy_platforms').delete().eq('id', id);
  if (error) throw error;
}

// ── Topic admin + course linking ────────────────────────────────────────────

/** Every managed topic, ordered for display (admin + catalog filters). */
export async function fetchAllTopics(): Promise<AcademyTopic[]> {
  const { data, error } = await supabase
    .from('academy_topics')
    .select('*')
    .order('position', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => mapTopic(r as Row));
}

export interface TopicCourseCounts {
  /** Courses linked to the topic whose status is published. */
  published: number;
  /** Every course linked to the topic, drafts included. */
  total: number;
}

/**
 * Course-link counts for every topic, keyed by topic id — used by the admin
 * Topics manager so an editor can see how many courses each topic actually
 * carries (published vs linked-including-drafts).
 */
export async function fetchTopicCourseCounts(): Promise<Map<string, TopicCourseCounts>> {
  const [linksRes, coursesRes] = await Promise.all([
    supabase.from('academy_course_topics').select('topic_id, course_id'),
    supabase.from('academy_courses').select('id, status'),
  ]);
  const statusById = new Map<string, string>();
  (coursesRes.data || []).forEach((r) => statusById.set(str((r as Row).id), str((r as Row).status)));
  const map = new Map<string, TopicCourseCounts>();
  (linksRes.data || []).forEach((r) => {
    const row = r as Row;
    const tid = str(row.topic_id);
    const entry = map.get(tid) || { published: 0, total: 0 };
    entry.total += 1;
    if (statusById.get(str(row.course_id)) === 'published') entry.published += 1;
    map.set(tid, entry);
  });
  return map;
}

/** The topic ids a course is currently linked to. */
export async function fetchCourseTopicIds(courseId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('academy_course_topics')
    .select('topic_id')
    .eq('course_id', courseId);
  if (error) throw error;
  return (data || []).map((r) => str((r as Row).topic_id));
}

/** Replaces a course's topic links with exactly the given topic ids. */
export async function setCourseTopics(courseId: string, topicIds: string[]): Promise<void> {
  const { error: delErr } = await supabase
    .from('academy_course_topics')
    .delete()
    .eq('course_id', courseId);
  if (delErr) throw delErr;
  if (!topicIds.length) return;
  const rows = topicIds.map((topicId) => ({ course_id: courseId, topic_id: topicId }));
  const { error } = await supabase.from('academy_course_topics').insert(rows);
  if (error) throw error;
}

export interface TopicInput {
  slug: string;
  name: string;
  summary: string;
  position: number;
}

function topicRow(input: TopicInput): Row {
  return {
    slug: input.slug,
    name: input.name,
    summary: input.summary,
    position: input.position,
  };
}

export async function insertTopic(input: TopicInput): Promise<AcademyTopic> {
  const { data, error } = await supabase.from('academy_topics').insert(topicRow(input)).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Topic was not created');
  return mapTopic(data as Row);
}

export async function updateTopic(id: string, input: TopicInput): Promise<AcademyTopic> {
  const { data, error } = await supabase
    .from('academy_topics')
    .update(topicRow(input))
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Topic was not updated');
  return mapTopic(data as Row);
}

/** Deletes a topic. Its course links cascade away with it. */
export async function deleteTopic(id: string): Promise<void> {
  const { error } = await supabase.from('academy_topics').delete().eq('id', id);
  if (error) throw error;
}

// ── Admin writes ────────────────────────────────────────────────────────────

export interface CourseInput {
  slug: string;
  title: string;
  summary: string;
  description: string;
  topic: string;
  level: string;
  visibility: AcademyVisibility;
  status: AcademyStatus;
  coverImage: string;
  seoTitle: string;
  seoDescription: string;
  position: number;
  estimatedMinutes: number;
  product?: string;
  learnerRole?: string;
  industry?: string;
  deliveryFormat?: string;
  contentType?: string;
  language?: string;
  versionApplicability?: string;
  availability?: string;
}

function courseRow(input: CourseInput): Row {
  return {
    slug: input.slug,
    title: input.title,
    summary: input.summary,
    description: input.description,
    topic: input.topic,
    level: input.level,
    visibility: input.visibility,
    status: input.status,
    cover_image: input.coverImage,
    seo_title: input.seoTitle,
    seo_description: input.seoDescription,
    position: input.position,
    estimated_minutes: input.estimatedMinutes,
    product: input.product ?? '',
    learner_role: input.learnerRole ?? '',
    industry: input.industry ?? '',
    delivery_format: input.deliveryFormat ?? '',
    content_type: input.contentType ?? '',
    language: input.language ?? 'en',
    version_applicability: input.versionApplicability ?? '',
    availability: input.availability ?? 'available',
    updated_at: new Date().toISOString(),
  };
}

export async function insertCourse(input: CourseInput): Promise<AcademyCourse> {
  const { data, error } = await supabase.from('academy_courses').insert(courseRow(input)).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Course was not created');
  return mapCourse(data as Row);
}

export async function updateCourse(id: string, input: CourseInput): Promise<AcademyCourse> {
  const { data, error } = await supabase
    .from('academy_courses').update(courseRow(input)).eq('id', id).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Course was not updated');
  return mapCourse(data as Row);
}

export async function updateCourseCover(id: string, coverImage: string): Promise<void> {
  const { error } = await supabase
    .from('academy_courses')
    .update({ cover_image: coverImage, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteCourse(id: string): Promise<void> {
  const { error } = await supabase.from('academy_courses').delete().eq('id', id);
  if (error) throw error;
}

export async function insertModule(courseId: string, title: string, position: number): Promise<AcademyModule> {
  const { data, error } = await supabase
    .from('academy_modules')
    .insert({ course_id: courseId, title, position, updated_at: new Date().toISOString() })
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Module was not created');
  return mapModule(data as Row, []);
}

export async function updateModule(id: string, patch: { title?: string; summary?: string; position?: number }): Promise<void> {
  const { error } = await supabase
    .from('academy_modules')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteModule(id: string): Promise<void> {
  const { error } = await supabase.from('academy_modules').delete().eq('id', id);
  if (error) throw error;
}

export interface LessonInput {
  courseId: string;
  moduleId: string | null;
  slug: string;
  title: string;
  summary: string;
  content: string;
  coverImage: string;
  videoProvider: string;
  videoId: string;
  durationSeconds: number;
  position: number;
  status: AcademyStatus;
}

function lessonRow(input: LessonInput): Row {
  return {
    course_id: input.courseId,
    module_id: input.moduleId,
    slug: input.slug,
    title: input.title,
    summary: input.summary,
    content: input.content,
    cover_image: input.coverImage,
    video_provider: input.videoProvider,
    video_id: input.videoId,
    duration_seconds: input.durationSeconds,
    position: input.position,
    status: input.status,
    updated_at: new Date().toISOString(),
  };
}

export async function insertLesson(input: LessonInput): Promise<AcademyLesson> {
  const { data, error } = await supabase.from('academy_lessons').insert(lessonRow(input)).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Lesson was not created');
  return mapLesson(data as Row);
}

export async function updateLesson(id: string, input: LessonInput): Promise<AcademyLesson> {
  const { data, error } = await supabase
    .from('academy_lessons').update(lessonRow(input)).eq('id', id).select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Lesson was not updated');
  return mapLesson(data as Row);
}

export async function deleteLesson(id: string): Promise<void> {
  const { error } = await supabase.from('academy_lessons').delete().eq('id', id);
  if (error) throw error;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** URL slugifier (lowercase, hyphenated). */
export function academySlugify(value: string): string {
  return (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function coursePath(courseSlug: string): string {
  return `/academy/courses/${courseSlug}`;
}

export function academyLessonPath(courseSlug: string, lessonSlug: string): string {
  return `/academy/learn/${courseSlug}/${lessonSlug}`;
}

/** Path to a platform overview page. */
export function platformPath(slug: string): string {
  return `/academy/platforms/${slug}`;
}

/** Academy home + catalog paths (single source so links never drift). */
export const ACADEMY_HOME_PATH = '/academy';
export const ACADEMY_CATALOG_PATH = '/academy/catalog';

/** Distinct topics across a course list, sorted. */
export function distinctTopics(courses: AcademyCourse[]): string[] {
  const set = new Set<string>();
  courses.forEach((c) => { const t = c.topic.trim(); if (t) set.add(t); });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Human duration, e.g. 7:12. */
export function formatAcademyDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Human course length, e.g. "~1h 30m". */
export function formatCourseLength(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `~${h}h ${m}m`;
  if (h) return `~${h}h`;
  return `~${m}m`;
}

/** Adjacent lessons within the flat, ordered course lesson list. */
export function adjacentLessons(
  lessons: AcademyLesson[],
  lessonId: string,
): { prev: AcademyLesson | null; next: AcademyLesson | null } {
  const index = lessons.findIndex((l) => l.id === lessonId);
  if (index === -1) return { prev: null, next: null };
  return {
    prev: index > 0 ? lessons[index - 1] : null,
    next: index < lessons.length - 1 ? lessons[index + 1] : null,
  };
}

// ── Video helpers ───────────────────────────────────────────────────────────

/** Cloudflare Stream customer subdomain (host only). */
const STREAM_HOST = 'customer-qqdvjs7hy46whegj.cloudflarestream.com';

export function streamIframeUrl(uid: string): string {
  return `https://${STREAM_HOST}/${uid}/iframe`;
}

export function streamThumbnailUrl(uid: string): string {
  return `https://${STREAM_HOST}/${uid}/thumbnails/thumbnail.jpg?time=2s&height=720`;
}

/** Embeddable iframe URL for a lesson (Stream or YouTube), or '' when none. */
export function lessonEmbedUrl(lesson: AcademyLesson): string {
  if (lesson.videoProvider === 'stream' && lesson.videoId) return streamIframeUrl(lesson.videoId);
  if (lesson.videoProvider === 'youtube' && lesson.videoId) return `https://www.youtube.com/embed/${lesson.videoId}`;
  return '';
}

/** Direct mp4 URL for a lesson, or '' when the provider is not mp4. */
export function lessonMp4Url(lesson: AcademyLesson): string {
  if (lesson.videoProvider === 'mp4' && lesson.videoId) return lesson.videoId;
  return '';
}

/** Poster image for a lesson (Stream thumbnail when available). */
export function lessonPosterUrl(lesson: AcademyLesson, fallback = ''): string {
  if (lesson.videoProvider === 'stream' && lesson.videoId) return streamThumbnailUrl(lesson.videoId);
  return fallback;
}